import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Product } from '../types';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFavorites } from '../hooks/useFavorites';
import { fetchProducts } from '../services/api';

// "Saved picks": the shopper's own shortlist, stored locally in their browser.
const SavedPage: React.FC = () => {
  const { favorites } = useFavorites();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      if (favorites.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const all = await fetchProducts({ limit: 1000 });
      if (!active) return;
      const order = new Map(favorites.map((id, i) => [id, i]));
      setProducts(
        all
          .filter((p) => order.has(String(p.id)))
          .sort((a, b) => (order.get(String(a.id)) ?? 0) - (order.get(String(b.id)) ?? 0))
      );
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [favorites]);

  return (
    <div className="bg-cream-100 min-h-screen">
      <SEO
        title="Saved picks"
        description="Your personal shortlist of clean, vetted products on GoodCart."
        url="https://goodcart.org/saved"
      />
      <div className="container-custom py-10 md:py-14">
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl text-earth-900">Saved picks</h1>
          <p className="text-earth-600 mt-2">
            {products.length > 0
              ? `${products.length} ${products.length === 1 ? 'product' : 'products'} you're keeping an eye on.`
              : 'Tap the heart on any product to keep it here.'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><LoadingSpinner /></div>
        ) : products.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-earth-100 p-10 md:p-16 text-center max-w-xl mx-auto"
          >
            <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-sage-50 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-7 w-7 text-sage-600"
                fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
              </svg>
            </div>
            <h2 className="font-display text-xl text-earth-900 mb-2">Nothing saved yet</h2>
            <p className="text-earth-600 mb-8">
              When something catches your eye, save it here so it's waiting for you next time.
            </p>
            <Link to="/search" className="btn btn-primary">Browse the collection</Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedPage;
